import React, { lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import { useProfileData, ProfileData } from './hooks/useProfileData';
import OriginalApp from './App';
import { usePublicPageDocumentScroll } from '@/hooks/usePublicPageDocumentScroll';

// Lazy load templates for code splitting
const MinimalTemplate = lazy(() => import('./templates/minimal-1/MinimalTemplate'));
const TechTemplate = lazy(() => import('./templates/tech-1/TechTemplate'));

// Template component type
type TemplateComponent = React.ComponentType<{ profileData: ProfileData }>;

// Template mapping
const TEMPLATES: Record<string, TemplateComponent> = {
  'professional-1': OriginalApp,
  'minimal-1': MinimalTemplate,
  'tech-1': TechTemplate
};

// Get template component based on template ID
const getTemplateComponent = (templateId: string | undefined): TemplateComponent => {
  if (!templateId) {
return OriginalApp;
}
  return TEMPLATES[templateId] || OriginalApp;
};

// Loading component
const ProfileLoading = () => (
  <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-emerald-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading profile...</p>
    </div>
  </div>
);

// Error component
const ProfileError = ({ error }: { error: string }) => (
  <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-red-50 to-gray-50">
    <div className="text-center max-w-md">
      <div className="mb-4">
        <svg className="w-16 h-16 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Profile Not Available</h1>
      <p className="text-gray-600">{error}</p>
    </div>
  </div>
);

export default function User() {
  usePublicPageDocumentScroll();
  const { slug } = useParams<{ slug: string }>();
  const { data, loading, error } = useProfileData(slug || '');

  // Show loading state
  if (loading) {
    return <ProfileLoading />;
  }

  // Show error state
  if (error || !data) {
    return <ProfileError error={error || 'Profile not found'} />;
  }

  // Get the appropriate template component based on websiteTemplate field
  const TemplateComponent = getTemplateComponent(data.websiteTemplate);

  // Render template with Suspense for lazy-loaded components
  return (
    <Suspense fallback={<ProfileLoading />}>
      <TemplateComponent profileData={data} />
    </Suspense>
  );
}

// Helper component for profile preview mode
export function UserPreview() {
  usePublicPageDocumentScroll();
  const { slug } = useParams<{ slug: string }>();
  const { data, loading, error } = useProfileData(slug || '');

  // Check if user is authenticated and owns this profile
  // This would normally check auth context
  const isOwner = true; // TODO: Check if current user owns this profile

  if (loading) {
    return <ProfileLoading />;
  }

  if (error || !data) {
    return <ProfileError error={error || 'Profile not found'} />;
  }

  // Get the appropriate template component based on websiteTemplate field
  const TemplateComponent = getTemplateComponent(data.websiteTemplate);

  return (
    <div>
      {/* Preview banner */}
      <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 text-center fixed top-0 left-0 right-0 z-[100]">
        <p className="text-sm text-yellow-800">
          <strong>Preview Mode:</strong> This is how your profile will appear to visitors
          {isOwner && (
            <button
              className="ml-4 text-yellow-600 hover:text-yellow-700 underline"
              onClick={() => window.close()}
            >
              Close Preview
            </button>
          )}
        </p>
      </div>

      {/* Render template with profile data */}
      <Suspense fallback={<ProfileLoading />}>
        <TemplateComponent profileData={data} />
      </Suspense>
    </div>
  );
}
